import { useEffect, useState } from 'react';
import { adminApi } from '@formosa/shared/api/admin';
import Pagination from '../../components/Pagination';
import ConfirmDialog from '../../components/ConfirmDialog';
import SysDictionaryEditModal from './SysDictionaryEditModal';
import {
  EMPTY_SYS_DIC_FILTERS,
  SYS_DIC_PAGE_SIZE,
  SYS_DIC_STATUS,
  SYS_DIC_STATUS_FILTER_OPTIONS,
  buildSysDicPageParams,
  formToSysDicPayload,
  formatSysDicStatus,
  sysDicStatusClassName,
} from './sysDicConstants';

const TABLE_COLUMNS = [
  'id',
  'Key',
  'Value',
  '備註',
  '狀態',
  '操作',
];

export default function SystemDicSettingPage() {
  const [filters, setFilters] = useState(EMPTY_SYS_DIC_FILTERS);
  const [records, setRecords] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');

  const [editOpen, setEditOpen] = useState(false);
  const [editRow, setEditRow] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [togglingId, setTogglingId] = useState(null);

  async function loadRecords(pageNo = 1, nextFilters = filters) {
    setLoading(true);
    setError('');
    try {
      const data = await adminApi.sysDictionaryPage(buildSysDicPageParams(pageNo, nextFilters));
      setRecords(data?.records || []);
      setPage(data?.current || pageNo);
      setTotalPages(data?.pages || 1);
      setTotal(data?.total ?? 0);
    } catch (err) {
      setError(err.message || '載入失敗');
      setRecords([]);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadRecords(1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function updateFilter(key, value) {
    setFilters((prev) => ({ ...prev, [key]: value }));
  }

  function handleSearch(event) {
    event.preventDefault();
    setMessage('');
    loadRecords(1, filters);
  }

  function handleReset() {
    const reset = { ...EMPTY_SYS_DIC_FILTERS };
    setFilters(reset);
    setMessage('');
    loadRecords(1, reset);
  }

  function openCreate() {
    setEditRow(null);
    setEditOpen(true);
    setMessage('');
  }

  function openEdit(row) {
    setEditRow(row);
    setEditOpen(true);
    setMessage('');
  }

  async function handleSave(form) {
    setSubmitting(true);
    setError('');
    try {
      const payload = formToSysDicPayload(form, editRow?.id);
      if (editRow?.id) {
        await adminApi.updateSysDictionary(payload);
        setMessage('已修改');
      } else {
        await adminApi.addSysDictionary(payload);
        setMessage('已新增');
      }
      setEditOpen(false);
      setEditRow(null);
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '保存失敗');
      throw err;
    } finally {
      setSubmitting(false);
    }
  }

  async function handleToggleStatus(row) {
    if (!row?.id) {
      return;
    }
    const nextStatus = row.status === SYS_DIC_STATUS.ENABLED
      ? SYS_DIC_STATUS.DISABLED
      : SYS_DIC_STATUS.ENABLED;
    setTogglingId(row.id);
    setError('');
    try {
      await adminApi.updateSysDictionary({
        id: row.id,
        status: nextStatus,
      });
      setRecords((prev) => prev.map((item) => (
        item.id === row.id ? { ...item, status: nextStatus } : item
      )));
      setMessage(nextStatus === SYS_DIC_STATUS.ENABLED ? '已啟用' : '已禁用');
    } catch (err) {
      setError(err.message || '狀態更新失敗');
    } finally {
      setTogglingId(null);
    }
  }

  async function handleConfirmDelete() {
    if (!deleteTarget?.id) {
      return;
    }
    setDeleting(true);
    setError('');
    try {
      await adminApi.deleteSysDictionary([deleteTarget.id]);
      setDeleteTarget(null);
      setMessage('已刪除');
      await loadRecords(page, filters);
    } catch (err) {
      setError(err.message || '刪除失敗');
    } finally {
      setDeleting(false);
    }
  }

  return (
    <div className="page-card">
      <div className="section-header">
        <h2>數據字典設置</h2>
      </div>

      <form className="filter-bar filter-bar-wrap" onSubmit={handleSearch}>
        <div className="form-field">
          <label htmlFor="sysDicFilterStatus">狀態</label>
          <select
            id="sysDicFilterStatus"
            value={filters.status}
            onChange={(e) => updateFilter('status', e.target.value)}
          >
            {SYS_DIC_STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value || 'all'} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>
        <div className="form-field">
          <label htmlFor="sysDicFilterKey">Key</label>
          <input
            id="sysDicFilterKey"
            type="text"
            value={filters.dicKey}
            onChange={(e) => updateFilter('dicKey', e.target.value)}
            placeholder="請輸入欄位名 Key"
          />
        </div>
        <div className="filter-actions filter-actions--end">
          <button type="submit" className="btn btn-primary">
            搜尋
          </button>
          <button type="button" className="btn btn-secondary" onClick={handleReset}>
            重置
          </button>
          <button type="button" className="btn btn-primary" onClick={openCreate}>
            新增
          </button>
        </div>
      </form>

      {message ? <p className="form-message form-message--success">{message}</p> : null}
      {error ? <p className="form-message form-message--error">{error}</p> : null}

      <div className="table-meta">共 {total} 筆</div>

      <div className="table-wrap">
        <table className="data-table">
          <thead>
            <tr>
              {TABLE_COLUMNS.map((col) => (
                <th key={col}>{col}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  載入中…
                </td>
              </tr>
            ) : records.length === 0 ? (
              <tr>
                <td colSpan={TABLE_COLUMNS.length} className="table-empty">
                  無資料
                </td>
              </tr>
            ) : (
              records.map((row) => (
                <tr key={row.id}>
                  <td>{row.id}</td>
                  <td className="mono-cell cell-wrap">{row.dicKey || '—'}</td>
                  <td className="cell-wrap">{row.dicValue || '—'}</td>
                  <td className="cell-wrap">{row.remark || '—'}</td>
                  <td>
                    <span className={sysDicStatusClassName(row.status)}>
                      {formatSysDicStatus(row.status)}
                    </span>
                  </td>
                  <td>
                    <div className="table-actions table-actions--news">
                      <div className="table-actions__row">
                        <button
                          type="button"
                          className="btn btn-success btn-sm"
                          onClick={() => openEdit(row)}
                        >
                          修改
                        </button>
                        <button
                          type="button"
                          className="btn btn-danger-outline btn-sm"
                          onClick={() => setDeleteTarget(row)}
                        >
                          刪除
                        </button>
                      </div>
                      <div className="table-actions__row">
                        {row.status === SYS_DIC_STATUS.ENABLED ? (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            disabled={togglingId === row.id}
                            onClick={() => handleToggleStatus(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--danger" aria-hidden="true">
                              ✕
                            </span>
                            禁用
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="btn btn-outline-neutral btn-sm btn-with-icon"
                            disabled={togglingId === row.id}
                            onClick={() => handleToggleStatus(row)}
                          >
                            <span className="btn-state-icon btn-state-icon--success" aria-hidden="true">
                              ✓
                            </span>
                            啟用
                          </button>
                        )}
                      </div>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <Pagination page={page} totalPages={totalPages} onChange={(next) => loadRecords(next, filters)} />

      <SysDictionaryEditModal
        open={editOpen}
        row={editRow}
        submitting={submitting}
        onClose={() => {
          if (!submitting) {
            setEditOpen(false);
            setEditRow(null);
          }
        }}
        onSubmit={handleSave}
      />

      <ConfirmDialog
        open={Boolean(deleteTarget)}
        title="刪除數據字典"
        message={deleteTarget ? `確定刪除 Key「${deleteTarget.dicKey}」？` : ''}
        confirmLabel={deleting ? '刪除中…' : '確認刪除'}
        confirming={deleting}
        onClose={() => !deleting && setDeleteTarget(null)}
        onConfirm={handleConfirmDelete}
      />
    </div>
  );
}
